import React, { useEffect, useRef } from "react";
import { CustomBarchart } from "../../components/CustomBarchart";
import { matrixTransformation } from "../util/matrixTransformation";
import { bradleyTerry } from "../util/bradleyTerry";
import { thurstoneMosteller } from "../util/thurstoneMosteller";
import { computePairBrackets } from "../util/computePairBrackets";

const HEADER_BG = "#374151";

const CARD     = { borderRadius: "8px", marginTop: "16px", width: "fit-content", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" };
const CARD_HDR = { background: HEADER_BG, padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px 8px 0 0" };
const SECTION  = { borderLeft: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" };
const FOOTER   = { ...SECTION, borderBottom: "1px solid #cbd5e1", borderRadius: "0 0 8px 8px", padding: "5px 14px 6px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" };

export default function BarchartQualityScore({ data, modelType }) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!data || !data.twoAfcData) return;

        const transformed = matrixTransformation(data);
        const rawLabels   = transformed.sortedMethods || [];
        const model       = modelType === "BT" ? bradleyTerry(transformed) : thurstoneMosteller(transformed);

        // rank order
        const order       = rawLabels.map((_, i) => i).sort((a, b) => model.betas[b] - model.betas[a]);
        const labels      = order.map(i => rawLabels[i]);
        const betasSorted = order.map(i => model.betas[i]);
        const seSorted    = order.map(i => model.se[i]);

        // sign-test p-values for every pair
        const pairBrackets = computePairBrackets(transformed.matrix, order);

        const node = CustomBarchart(betasSorted, labels, {
            se: seSorted,
            betas: betasSorted,
            covFull: model.covFull,
            showCI: true,
            showThresholdLines: false,
            yAxisLabel: "Quality score (β)",
            legendText: "Quality scores (95% CI)",
            width: 700,
            height: 350,
            barOpacity: 0.55,
            positiveColor: "#2563eb",
            negativeColor: "#f05252",
            pairBrackets: pairBrackets
        });

        const container = containerRef.current;
        container.innerHTML = "";
        container.appendChild(node);
    }, [data, modelType]);

    const modelTag = modelType === "BT" ? "Bradley–Terry" : "Thurstone–Mosteller";

    return (
        <div style={CARD}>

            <div style={CARD_HDR}>
                <h4 style={{ margin: 0, color: "white", fontSize: "13px", fontWeight: "600" }}>Quality Score Barchart</h4>
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "10px", padding: "1px 6px", borderRadius: "4px" }}>
                    {modelTag}
                </span>
            </div>

            <div style={{ ...SECTION, padding: "4px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280" }}>
                Raw β (quality score) with 95% CI.
            </div>

            {/* chart */}
            <div style={{ ...SECTION, padding: "12px 16px 16px", background: "white" }}>
                <div ref={containerRef} style={{ maxWidth: "900px" }} />
            </div>

            <div style={FOOTER}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ width: "14px", height: "14px", background: "#2563eb", opacity: 0.7, display: "inline-block", borderRadius: "2px" }} />
                    positive β (above-average quality)
                </span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ width: "14px", height: "14px", background: "#f05252", opacity: 0.7, display: "inline-block", borderRadius: "2px" }} />
                    negative β (below-average quality)
                </span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <svg width="24" height="8" style={{ overflow: "visible" }}>
                        <line x1="0" y1="4" x2="24" y2="4" stroke="#2563eb" strokeWidth="2" />
                    </svg>
                    significant pair (p &lt; 0.05)
                </span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <svg width="24" height="8" style={{ overflow: "visible" }}>
                        <line x1="0" y1="4" x2="24" y2="4" stroke="#dc2626" strokeWidth="2" strokeDasharray="4,3" />
                    </svg>
                    not significant (p ≥ 0.05)
                </span>
            </div>
        </div>
    );
}
